import React from 'react';
import styled, { css } from 'styled-components';
import { Camera, MapPin, PenTool, QrCode, Tag, Repeat, ClipboardList, AlertTriangle, X as XIcon, Mic } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { ThemedText, Button, Input } from '../';

const FieldContainer = styled.div`
  position: relative;
  padding: ${props => props.$deemphasized ? props.theme.spacing.sm : props.theme.spacing.md};
  margin-bottom: ${props => props.$deemphasized ? props.theme.spacing.sm : props.theme.spacing.md};
  border: ${props => props.$deemphasized ? '1px dashed' : '2px solid'} ${props => props.isSelected ? props.theme.colors.tint : (props.$deemphasized ? props.theme.colors.border : 'transparent')};
  border-radius: ${props => props.theme?.borderRadius?.md};
  background-color: ${props => props.$deemphasized ? props.theme.colors.backgroundSecondary : props.theme.colors.background};
  box-shadow: ${props => props.isSelected ? `0 0 0 4px ${props.theme.colors.tint}20` : (props.$deemphasized ? 'none' : props.theme.shadows.sm)};
  cursor: pointer;
  opacity: ${props => props.$deemphasized && !props.isSelected ? 0.65 : 1};
  transition: all 0.2s ease;

  ${props => props.$sectionStart && css`
    margin-top: ${props.theme.spacing.xl};

    &::before {
      content: '🧪  Lab Results · auto-populated from linked lab reports';
      display: block;
      position: absolute;
      top: calc(-1 * ${props.theme.spacing.lg});
      left: 0;
      right: 0;
      font-size: ${props.theme.typography.sizes.small};
      font-weight: 600;
      letter-spacing: 0.02em;
      color: ${props.theme.colors.muted};
      padding-bottom: ${props.theme.spacing.xs};
      border-bottom: 1px dashed ${props.theme.colors.border};
    }
  `}

  &:hover {
    border-color: ${props => props.isSelected ? props.theme.colors.tint : props.theme.colors.tint + '50'};
    transform: ${props => props.$deemphasized ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.$deemphasized ? 'none' : props.theme.shadows.md};
    opacity: 1;
  }
`;

const DeleteButton = styled(Button)`
  position: absolute;
  top: -10px;
  right: -10px;
  z-index: 10;
  min-width: 28px;
  min-height: 28px;
  width: 28px;
  height: 28px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  line-height: 1;
  border-radius: 50%;
  box-shadow: ${props => props.theme.shadows.sm};
  background-color: ${props => props.theme.colors.danger};
  color: white;
  border: 2px solid white;

  &:hover {
    background-color: ${props => props.theme.colors.danger};
    transform: scale(1.1);
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme?.borderRadius?.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme?.typography?.sizes?.default};
  min-height: 40px;

  &:disabled {
    background-color: ${props => props.theme.colors.backgroundSecondary};
    opacity: 0.6;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const StyledCheckbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${props => props.theme.colors.tint};
`;


const RadioOption = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const StyledRadio = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${props => props.theme.colors.tint};
`;

const MockFieldContainer = styled.div`
  border: 2px dashed ${props => props.theme.colors.border};
  border-radius: ${props => props.theme?.borderRadius?.md};
  padding: ${props => props.theme.spacing.lg};
  text-align: center;
  background-color: ${props => props.theme.colors.backgroundSecondary};
  min-height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.muted};
  cursor: pointer;

  &:hover {
    border-color: ${props => props.theme.colors.tint};
    background-color: ${props => props.theme.colors.tint}10;
  }
`;

const MockIcon = styled.div`
  opacity: 0.6;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const FormFieldRenderer = ({ field, isSelected, onSelect, onUpdate, onDelete, deemphasized = false, sectionStart = false }) => {
  const theme = useTheme();

  const renderField = () => {
    const { props } = field;

    switch (field.type) {
      case 'input':
        return (
          <Input
            label={props.label + (props.required ? ' *' : '')}
            type="text"
            placeholder={props.placeholder}
            disabled
            value=""
          />
        );

      case 'number':
        return (
          <Input
            label={props.label + (props.required ? ' *' : '')}
            type="number"
            step="any"
            placeholder={props.placeholder}
            disabled
            value=""
          />
        );

      case 'date':
      case 'time':
      case 'datetime': {
        const htmlType = props.precision === 'date' ? 'date'
                       : props.precision === 'time' ? 'time'
                       : 'datetime-local';
        return (
          <Input
            label={props.label + (props.required ? ' *' : '')}
            type={htmlType}
            disabled
            value=""
          />
        );
      }

      case 'select':
        return (
          <div>
            <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.sm }}>
              {props.label + (props.required ? ' *' : '')}
            </ThemedText>
            <StyledSelect theme={theme} disabled>
              <option>Choose...</option>
              {props.options?.map((opt, idx) => (
                <option key={idx} value={opt.value}>{opt.label}</option>
              ))}
            </StyledSelect>
          </div>
        );

      case 'textarea':
        return (
          <Input
            label={props.label + (props.required ? ' *' : '')}
            type="textarea"
            placeholder={props.placeholder}
            rows={props.rows}
            disabled
            value=""
          />
        );

      case 'toggle':
        return (
          <CheckboxContainer theme={theme}>
            <StyledCheckbox
              theme={theme}
              type="checkbox"
              disabled
              defaultChecked={props.defaultValue}
            />
            <ThemedText>{props.label}</ThemedText>
          </CheckboxContainer>
        );

      case 'checkbox_group':
        return (
          <div>
            <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.sm }}>
              {props.label + (props.required ? ' *' : '')}
            </ThemedText>
            {props.options?.map((opt, idx) => (
              <CheckboxContainer key={idx} theme={theme}>
                <StyledCheckbox
                  theme={theme}
                  type="checkbox"
                  disabled
                />
                <ThemedText>{opt.label}</ThemedText>
              </CheckboxContainer>
            ))}
          </div>
        );

      case 'radio':
        return (
          <div>
            <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.sm }}>
              {props.label + (props.required ? ' *' : '')}
            </ThemedText>
            {props.options?.map((opt, idx) => (
              <RadioOption key={idx} theme={theme}>
                <StyledRadio
                  theme={theme}
                  type="radio"
                  name={field.id}
                  value={opt.value}
                  disabled
                />
                <ThemedText>{opt.label}</ThemedText>
              </RadioOption>
            ))}
          </div>
        );

      case 'tags':
        return (
          <div>
            <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.sm }}>
              {props.label + (props.required ? ' *' : '')}
            </ThemedText>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: theme.spacing.xs }}>
              {props.options?.length > 0
                ? props.options.slice(0, 5).map(opt => (
                    <span key={opt.value} style={{
                      background: opt.color || theme.colors.tint,
                      color: '#fff',
                      borderRadius: '12px',
                      padding: '2px 10px',
                      fontSize: '12px'
                    }}>{opt.label}</span>
                  ))
                : ['Example', 'Tags', 'Here'].map(t => (
                    <span key={t} style={{
                      background: theme.colors.tint,
                      color: '#fff',
                      borderRadius: '12px',
                      padding: '2px 10px',
                      fontSize: '12px'
                    }}>{t}</span>
                  ))
              }
            </div>
            {props.allowCustom !== false && (
              <ThemedText type="small" style={{ color: theme.colors.muted }}>
                + Custom tags allowed
              </ThemedText>
            )}
          </div>
        );

      case 'image_camera':
        return (
          <div>
            <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.sm }}>
              {props.label + (props.required ? ' *' : '')}
            </ThemedText>
            <MockFieldContainer theme={theme}>
              <MockIcon><Camera size={32} /></MockIcon>
              <ThemedText>{props.placeholder || 'Tap to capture photo...'}</ThemedText>
              <ThemedText type="small" style={{ color: theme.colors.muted }}>
                Camera only - mobile implementation
              </ThemedText>
            </MockFieldContainer>
          </div>
        );

      case 'geolocation':
        return (
          <div>
            <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.sm }}>
              {props.label + (props.required ? ' *' : '')}
            </ThemedText>
            <MockFieldContainer theme={theme}>
              <MockIcon><MapPin size={32} /></MockIcon>
              <ThemedText>{props.placeholder || 'Tap to capture location...'}</ThemedText>
              <ThemedText type="small" style={{ color: theme.colors.muted }}>
                GPS location capture ({props.accuracy} accuracy)
              </ThemedText>
            </MockFieldContainer>
          </div>
        );

      case 'drawing':
        return (
          <div>
            <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.sm }}>
              {props.label + (props.required ? ' *' : '')}
            </ThemedText>
            <MockFieldContainer theme={theme}>
              <MockIcon><PenTool size={32} /></MockIcon>
              <ThemedText>{props.placeholder || 'Tap to draw or add signature...'}</ThemedText>
              <ThemedText type="small" style={{ color: theme.colors.muted }}>
                Drawing field - mobile implementation
              </ThemedText>
            </MockFieldContainer>
          </div>
        );

      case 'voice':
        return (
          <div>
            <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.xs }}>
              {props.label + (props.required ? ' *' : '')}
            </ThemedText>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.sm,
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius?.md || 8,
                background: theme.colors.backgroundSecondary || theme.colors.background,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  background: theme.colors.danger,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Mic size={16} color="#fff" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <ThemedText type="small">
                  {props.placeholder || 'Tap to record'}
                </ThemedText>
                <ThemedText type="small" style={{ color: theme.colors.muted, fontSize: 11 }}>
                  max {props.maxDurationSeconds ?? 300}s
                  {props.enableTranscription !== false
                    ? ` · transcribe (${props.transcriptionLanguage || 'en-US'})`
                    : ' · transcribe off'}
                </ThemedText>
              </div>
            </div>
          </div>
        );

      case 'qr_scanner':
        return (
          <div>
            <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.sm }}>
              {props.label + (props.required ? ' *' : '')}
            </ThemedText>
            <MockFieldContainer theme={theme}>
              <MockIcon><QrCode size={32} /></MockIcon>
              <ThemedText>{props.placeholder || 'Tap to scan QR code...'}</ThemedText>
              <ThemedText type="small" style={{ color: theme.colors.muted }}>
                QR code scanner - mobile implementation
              </ThemedText>
              {props.validationRules?.rules?.length > 0 && (
                <ThemedText type="small" style={{ color: theme.colors.warning, marginTop: theme.spacing.xs }}>
                  <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />{props.validationRules.rules.length} validation rule(s) configured
                </ThemedText>
              )}
            </MockFieldContainer>
          </div>
        );

      case 'barcode_scanner':
        return (
          <div>
            <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.sm }}>
              {props.label + (props.required ? ' *' : '')}
            </ThemedText>
            <MockFieldContainer theme={theme}>
              <MockIcon><Tag size={32} /></MockIcon>
              <ThemedText>{props.placeholder || 'Tap to scan barcode...'}</ThemedText>
              <ThemedText type="small" style={{ color: theme.colors.muted }}>
                Barcode scanner - mobile implementation
                {props.barcodeFormat && props.barcodeFormat !== 'any' && (
                  <> ({props.barcodeFormat.toUpperCase()})</>
                )}
              </ThemedText>
              {props.validationRules?.rules?.length > 0 && (
                <ThemedText type="small" style={{ color: theme.colors.warning, marginTop: theme.spacing.xs }}>
                  <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />{props.validationRules.rules.length} validation rule(s) configured
                </ThemedText>
              )}
            </MockFieldContainer>
          </div>
        );

      case 'repeated_form':
        const config = props.configuration || {};
        const templateName = config.repeatedFormDisplayName || 'Form Instance';
        const minInstances = config.minInstances || 0;
        const maxInstances = config.maxInstances || 10;

        return (
          <div>
            <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.sm }}>
              {props.label + (props.required ? ' *' : '')}
            </ThemedText>
            <MockFieldContainer theme={theme}>
              <MockIcon><ClipboardList size={32} /></MockIcon>
              <ThemedText><Repeat size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Repeated Form Field</ThemedText>
              {config.repeatedFormSchemaId ? (
                <>
                  <ThemedText type="small" style={{ color: theme.colors.tint }}>
                    Template: {templateName}
                  </ThemedText>
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>
                    Instances: {minInstances} to {maxInstances}
                  </ThemedText>
                </>
              ) : (
                <ThemedText type="small" style={{ color: theme.colors.warning }}>
                  <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />No template selected
                </ThemedText>
              )}
            </MockFieldContainer>
          </div>
        );

      case 'divider':
        return (
          <hr style={{
            border: 'none',
            height: '1px',
            backgroundColor: theme.colors.border,
            margin: `${theme.spacing.lg} 0`
          }} />
        );

      case 'header':
        const HeaderTag = props.level || 'h3';
        return (
          <ThemedText
            as={HeaderTag}
            type="subtitle"
            style={{
              textAlign: props.align || 'left',
              marginTop: theme.spacing.md,
              marginBottom: theme.spacing.md
            }}
          >
            {props.text}
          </ThemedText>
        );

      default:
        return (
          <div style={{
            padding: theme.spacing.md,
            backgroundColor: theme.colors.warning + '20',
            border: `1px solid ${theme.colors.warning}`,
            borderRadius: theme?.borderRadius?.md,
            color: theme.colors.warning
          }}>
            Unknown field type: {field.type}
          </div>
        );
    }
  };

  return (
    <FieldContainer
      theme={theme}
      isSelected={isSelected}
      $deemphasized={deemphasized}
      $sectionStart={sectionStart}
      onClick={() => onSelect(field)}
    >
      {renderField()}
      {isSelected && (
        <DeleteButton
          theme={theme}
          variant="danger"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(field.id);
          }}
        >
          <XIcon size={14} />
        </DeleteButton>
      )}
    </FieldContainer>
  );
};